type AreaRect = { top: number; left: number; width: number; height: number };
type Area = { key: string; name: string; color?: string; rect?: AreaRect; points?: Array<{ x: number; y: number }> };

type Props = {
  imageUrl: string;
  areas: (Area & { points?: Array<{ x: number; y: number }> })[];
  selectedKey?: string;
  onSelect?: (key: string) => void;
  viewWidth?: number; // if using pixel coordinates
  viewHeight?: number;
};

const rectToPointArray = (r: AreaRect): Array<{x:number;y:number}> => {
  const x1 = r.left, y1 = r.top;
  const x2 = r.left + r.width, y2 = r.top + r.height;
  return [ {x:x1,y:y1}, {x:x2,y:y1}, {x:x2,y:y2}, {x:x1,y:y2} ];
};

const pointsToString = (pts: Array<{x:number;y:number}>): string => pts.map(p => `${p.x},${p.y}`).join(' ');

const centroid = (pts: Array<{x:number;y:number}>) => {
  if (!pts.length) return { x: 0, y: 0 };
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
};

const hexToRgba = (hex?: string, alpha = 0.28) => {
  if (!hex) return `rgba(79,140,255,${alpha})`;
  let h = hex.replace('#','');
  if (h.length === 3) h = h.split('').map(c=>c+c).join('');
  const r = parseInt(h.slice(0,2),16);
  const g = parseInt(h.slice(2,4),16);
  const b = parseInt(h.slice(4,6),16);
  return `rgba(${r},${g},${b},${alpha})`;
};

import { useEffect, useState } from 'react';

export default function FloorplanOverlay({ imageUrl, areas, selectedKey, onSelect, viewWidth = 100, viewHeight = 100 }: Props) {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    if (!imageUrl) { setNatural(null); return; }
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth || viewWidth, h: img.naturalHeight || viewHeight });
    img.onerror = () => setNatural({ w: viewWidth, h: viewHeight });
    img.src = imageUrl;
    return () => { img.onload = null; img.onerror = null; };
  }, [imageUrl, viewWidth, viewHeight]);

  const dims = { w: natural?.w || viewWidth, h: natural?.h || viewHeight };

  return (
    <div className="ah-floor-canvas">
      <svg viewBox={`0 0 ${dims.w} ${dims.h}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', display: 'block' }}>
        <image href={imageUrl} x={0} y={0} width={dims.w} height={dims.h} />
        {areas.map((a) => {
          const pts = a.rect ? rectToPointArray(a.rect) : (a.points as any) as Array<{x:number;y:number}>;
          if (!pts || !pts.length) return null;
          const points = pointsToString(pts);
          const isActive = selectedKey === a.key;
          const stroke = a.color || (isActive ? '#4f8cff' : 'rgba(79,140,255,0.6)');
          const fill = hexToRgba(a.color, 0.28);
          const c = centroid(pts);
          return (
            <g key={a.key} className="area">
              <polygon
                points={points}
                style={{ fill, stroke, strokeWidth: 1.5, cursor: 'pointer' }}
                onClick={() => onSelect?.(a.key)}
              />
              <text
                x={c.x}
                y={c.y}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 24, fontWeight: 600, fill: 'white', pointerEvents: 'none', paintOrder: 'stroke' as any, stroke: 'black', strokeWidth: 3 }}
              >
                {a.name}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
